<template>
  <span class="typewriter-text">
    {{ displayText }}
    <span v-if="isTyping && showCursor" class="cursor">|</span>
  </span>
</template>

<script setup>
import { ref, watch, onMounted, onUnmounted } from 'vue'

const props = defineProps({
  text: {
    type: String,
    required: true
  },
  speed: {
    type: Number,
    default: 50
  },
  autoStart: {
    type: Boolean,
    default: true
  },
  showCursor: {
    type: Boolean,
    default: true
  },
  onComplete: {
    type: Function,
    default: null
  }
})

const emit = defineEmits(['complete', 'start'])

// 状态
const displayText = ref('')
const currentIndex = ref(0)
const isTyping = ref(false)
const timer = ref(null)
const cursorTimer = ref(null)

// 开始打字效果
const startTyping = () => {
  if (isTyping.value || !props.text) return
  
  isTyping.value = true
  currentIndex.value = 0
  displayText.value = ''
  
  emit('start')
  
  typeNextChar()
}

// 打字下一个字符
const typeNextChar = () => {
  if (currentIndex.value >= props.text.length) {
    // 打字完成
    isTyping.value = false
    emit('complete')
    props.onComplete?.()
    return
  }
  
  displayText.value += props.text[currentIndex.value]
  currentIndex.value++
  
  timer.value = setTimeout(typeNextChar, props.speed)
}

// 停止打字效果
const stopTyping = () => {
  if (timer.value) {
    clearTimeout(timer.value)
    timer.value = null
  }
  isTyping.value = false
}

// 重置打字效果
const resetTyping = () => {
  stopTyping()
  displayText.value = ''
  currentIndex.value = 0
}

// 立即显示全部文字
const showFullText = () => {
  stopTyping()
  displayText.value = props.text
  currentIndex.value = props.text.length
  isTyping.value = false
  emit('complete')
  props.onComplete?.()
}

// 监听文字变化
watch(() => props.text, (newText) => {
  if (newText) {
    resetTyping()
    if (props.autoStart) {
      // 稍微延迟开始，让组件有时间渲染
      setTimeout(startTyping, 100)
    }
  }
}, { immediate: true })

// 光标闪烁效果
const startCursorBlink = () => {
  if (cursorTimer.value) {
    clearInterval(cursorTimer.value)
  }
  
  cursorTimer.value = setInterval(() => {
    const cursor = document.querySelector('.cursor')
    if (cursor) {
      cursor.style.opacity = cursor.style.opacity === '0' ? '1' : '0'
    }
  }, 500)
}

onMounted(() => {
  if (props.showCursor) {
    startCursorBlink()
  }
})

onUnmounted(() => {
  stopTyping()
  if (cursorTimer.value) {
    clearInterval(cursorTimer.value)
  }
})

// 暴露方法给父组件
defineExpose({
  startTyping,
  stopTyping,
  resetTyping,
  showFullText
})
</script>

<style lang="scss" scoped>
.typewriter-text {
  display: inline-block;
  
  .cursor {
    display: inline-block;
    animation: blink 1s infinite;
    color: #1989fa;
    font-weight: bold;
  }
}

@keyframes blink {
  0%, 50% {
    opacity: 1;
  }
  51%, 100% {
    opacity: 0;
  }
}
</style>